// My Pay — the employee pay & financial workspace. This shell owns the
// URL-backed top-level tabs (Overview / Payslips / Loans & Advances / Reports),
// the persisted amount-privacy control and the payslip backbone; each tab
// composes its own reads with section-level partial-data states. Mounted on the
// splat route `my-pay/*` (see TenantLayout).
import React, { Suspense, lazy, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { Wallet, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger } from '@/features/shared/components/ui/tabs';
import { Button } from '@/features/shared/components/ui/button';
import { Skeleton } from '@/features/shared/components/ui/skeleton';
import { useEntitlements } from '@/features/entitlements/useEntitlements';
import { useMarketplaceVisible } from '@/features/marketplace/useMarketplaceVisible';
import { track } from '@/lib/telemetry';
import { useMyPayslips, useMyEmployeeId, useMyPayslip, useMyCompensation } from '../api/hooks';
import { COMPENSATION_DISABLED, isEmployeeNotLinked } from '../api/payApi';
import { NotLinkedState } from '../components/NotLinkedState';
import { useAmountPrivacy } from '../lib/privacy';
import { useMyPayNav, type MyPayTab } from '../lib/nav';
import { InsightsDrawer } from '../components/InsightsDrawer';
import { MyPayBoundary } from '../components/MyPayBoundary';
import { OverviewTab } from './OverviewTab';
import { LazySection } from '@/features/shared/components/recovery/LazySection';
import { SectionFailed } from '@/features/shared/components/recovery/SectionFailed';

// Lower-priority tabs load on demand.
const PayslipsTab = lazy(() => import('./PayslipsTab'));
const LoansTab = lazy(() => import('./LoansTab'));
const CompensationTab = lazy(() => import('./CompensationTab'));
const ReportsTab = lazy(() => import('./ReportsTab'));
const EarningsTab = lazy(() => import('./EarningsTab'));

const MyPayContent: React.FC = () => {
  const { t } = useTranslation('payroll');
  const { base, tab, goTab } = useMyPayNav();
  const { revealed, toggle } = useAmountPrivacy();

  const meQ = useMyEmployeeId();
  const payslipsQ = useMyPayslips();
  const payslips = payslipsQ.data ?? [];
  const isLoading = meQ.isLoading || (!!meQ.data && payslipsQ.isLoading);
  // No employee record linked to this account on the payroll side: every self read
  // answers 404 not_found. A set-up state, not an outage — checked before isError.
  const notLinked = payslipsQ.isError && isEmployeeNotLinked(payslipsQ.error);
  const isError = payslipsQ.isError && !notLinked;
  const noProfile = meQ.isError || (meQ.isFetched && !meQ.data);
  const refetch = () => (meQ.data ? payslipsQ.refetch() : meQ.refetch());

  // Loans & Advances surfaces: the same two-key gate as the standalone module
  // (tenant credit entitlement ∧ the employer's employee-loans toggle).
  const { hasProduct } = useEntitlements();
  const hasCredit = hasProduct('credit');
  const { loaded: visLoaded, loansVisible } = useMarketplaceVisible(hasCredit);
  const creditEnabled = hasCredit && loansVisible;

  // Compensation tab visibility: probe once; a policy-disabled tenant hides the
  // tab entirely (server enforces regardless).
  const compQ = useMyCompensation();
  const compDisabled = compQ.isError && (compQ.error as Error)?.message === COMPENSATION_DISABLED;

  const detailForChip = useMyPayslip(payslips[0]?.id ?? null);
  const currencyChip = detailForChip.data?.currency ?? payslips[0]?.currency;
  const countryChip = detailForChip.data?.country;

  useEffect(() => {
    track('my_pay_opened');
  }, []);
  useEffect(() => {
    track('my_pay_tab', { tab });
  }, [tab]);

  const onToggleReveal = () => {
    track('amounts_toggled', { revealed: !revealed });
    toggle();
  };

  // A deep link to a hidden tab falls back to the Overview.
  if (tab === 'loans' && visLoaded && !creditEnabled) {
    return <Navigate to={base} replace />;
  }
  if (tab === 'compensation' && compDisabled) {
    return <Navigate to={base} replace />;
  }

  if (notLinked) {
    return (
      <div className="space-y-6">
        <Header />
        <NotLinkedState />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-6">
        <Header chip={currencyChip} country={countryChip} />
        <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
          <AlertCircle className="h-8 w-8 text-[#991B1B]" />
          <p className="text-charcoal">{t('myPay.loadError')}</p>
          <Button variant="outline" onClick={() => refetch()}>{t('myPay.retry')}</Button>
        </div>
      </div>
    );
  }

  if (!isLoading && (noProfile || payslips.length === 0) && tab !== 'earnings') {
    return (
      <div className="space-y-6">
        <Header chip={currencyChip} country={countryChip} />
        <div className="flex flex-col items-center justify-center gap-2 py-20 text-center">
          <Wallet className="h-10 w-10 text-muted-foreground" />
          <p className="font-medium text-charcoal">
            {noProfile ? t('myPay.noProfileTitle') : t('myPay.noStatementsTitle')}
          </p>
          <p className="text-sm text-muted-foreground">
            {noProfile ? t('myPay.noProfileBody') : t('myPay.noStatementsBody')}
          </p>
          {!noProfile && (
            <Button variant="outline" size="sm" onClick={() => goTab('earnings')}>
              {t('myPay.earnings.seeAll')}
            </Button>
          )}
        </div>
      </div>
    );
  }

  const tabFallback = (
    <div className="space-y-4">
      {[0, 1, 2].map((i) => <Skeleton key={i} className="h-24 w-full rounded-xl" />)}
    </div>
  );

  return (
    <div className="space-y-6">
      <Header
        chip={currencyChip}
        country={countryChip}
        action={
          <>
            <Button variant="ghost" size="sm" className="gap-1.5" onClick={onToggleReveal}>
              {revealed ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              <span className="text-xs">{revealed ? t('myPay.hideAmounts') : t('myPay.showAmounts')}</span>
            </Button>
            <InsightsDrawer payslips={payslips} revealed={revealed} />
          </>
        }
      />

      <Tabs value={tab} onValueChange={(v) => goTab(v as MyPayTab)}>
        <TabsList className="w-full justify-start overflow-x-auto sm:w-auto">
          <TabsTrigger value="overview">{t('myPay.tabOverview')}</TabsTrigger>
          <TabsTrigger value="payslips">{t('myPay.tabPayslips')}</TabsTrigger>
          <TabsTrigger value="earnings">{t('myPay.tabEarnings')}</TabsTrigger>
          {creditEnabled && <TabsTrigger value="loans">{t('myPay.tabLoans')}</TabsTrigger>}
          {!compDisabled && <TabsTrigger value="compensation">{t('myPay.tabCompensation')}</TabsTrigger>}
          <TabsTrigger value="reports">{t('myPay.tabReports')}</TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === 'overview' && (
        <OverviewTab
          payslips={payslips}
          loading={isLoading}
          revealed={revealed}
          onToggleReveal={onToggleReveal}
          creditEnabled={creditEnabled}
        />
      )}
      {tab === 'payslips' && (
        <LazySection fallback={() => <SectionFailed compact />}>
        <Suspense fallback={tabFallback}>
          <PayslipsTab payslips={payslips} loading={isLoading} revealed={revealed} />
        </Suspense>
        </LazySection>
      )}
      {tab === 'earnings' && (
        <LazySection fallback={() => <SectionFailed compact />}>
        <Suspense fallback={tabFallback}>
          <EarningsTab revealed={revealed} />
        </Suspense>
        </LazySection>
      )}
      {tab === 'loans' && creditEnabled && (
        <LazySection fallback={() => <SectionFailed compact />}>
        <Suspense fallback={tabFallback}>
          <LoansTab revealed={revealed} />
        </Suspense>
        </LazySection>
      )}
      {tab === 'compensation' && !compDisabled && (
        <LazySection fallback={() => <SectionFailed compact />}>
        <Suspense fallback={tabFallback}>
          <CompensationTab revealed={revealed} />
        </Suspense>
        </LazySection>
      )}
      {tab === 'reports' && (
        <LazySection fallback={() => <SectionFailed compact />}>
        <Suspense fallback={tabFallback}>
          <ReportsTab payslips={payslips} loading={isLoading} revealed={revealed} />
        </Suspense>
        </LazySection>
      )}
    </div>
  );
};

export const MyPayPage: React.FC = () => (
  <MyPayBoundary>
    <MyPayContent />
  </MyPayBoundary>
);

const Header: React.FC<{ chip?: string; country?: string; action?: React.ReactNode }> = ({ chip, country, action }) => {
  const { t } = useTranslation('payroll');
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-6">
      <div>
        <h1 className="text-h1 text-charcoal">{t('myPay.title')}</h1>
        <p className="text-muted-foreground mt-1">{t('myPay.workspaceDescription')}</p>
      </div>
      <div className="flex items-center gap-3">
        {chip && (
          <span className="rounded-full border border-border bg-card px-3 py-1 text-sm font-medium text-muted-foreground">
            {country ? `${country} · ` : ''}{chip}
          </span>
        )}
        {action}
      </div>
    </div>
  );
};
